import { MapPin, Phone, EnvelopeSimple, Briefcase, Newspaper } from "@phosphor-icons/react";
import { WHY, SCHOOLS } from "./siteData";
import { useSite, useSettings, imgUrl } from "../../lib/site";

const telHref = (p) => {
  const d = p.replace(/[^\d+]/g, "");
  return `tel:${d.length === 10 ? `+91${d}` : d}`;
};

const useSchools = () => {
  const { settings } = useSettings();
  const bySlug = Object.fromEntries((settings?.schools || []).map((s) => [s.slug, s]));
  return { settings, schools: SCHOOLS.map((s) => ({ ...s, details: "", address: "", phones: [], email: "", ...bySlug[s.slug] })) };
};

const Title = ({ k, t, sub }) => (
  <div className="text-center mb-10">
    <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#E39A0B]">{k}</div>
    <h2 className="font-display text-3xl md:text-4xl font-black text-[#0B2A5C] mt-2">{t}</h2>
    {sub && <p className="text-zinc-600 mt-2 max-w-2xl mx-auto">{sub}</p>}
  </div>
);

export const Why = () => (
  <section className="py-16 px-4">
    <Title k="Our Promise" t="Why Choose Our Platform?" />
    <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:divide-x divide-zinc-200">
      {WHY.map((w) => (
        <div key={w.t} className="flex items-center gap-4 px-4">
          <w.Icon size={40} weight="fill" className="text-[#0B3A8C] shrink-0" />
          <div className="text-sm font-medium text-zinc-800">{w.t}</div>
        </div>
      ))}
    </div>
  </section>
);

export { About } from "./AboutSection";

export const Gallery = () => {
  const { items } = useSite("gallery");
  return (
  <section id="gallery" className="py-20 px-4 scroll-mt-24">
    <Title k="Photo Gallery" t="Life on campus" sub="Moments from our classrooms, grounds and celebrations." />
    <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-4">
      {items.map((g, i) => (
        <figure key={g.id} className="relative overflow-hidden rounded-xl group" data-testid={`gallery-item-${i}`}>
          <img src={imgUrl(g.url)} alt={g.caption} loading="lazy" className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" />
          <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-white text-sm p-3">{g.caption}</figcaption>
        </figure>
      ))}
    </div>
  </section>
  );
};

export const Media = () => {
  const { items } = useSite("news");
  return (
  <section id="media" className="py-20 px-4 bg-white scroll-mt-24">
    <Title k="Media Point" t="News, circulars & press" />
    <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-5">
      {items.map((m, i) => (
        <article key={m.id} className="border border-zinc-200 rounded-xl p-6 hover:shadow-sm transition-shadow" data-testid={`media-item-${i}`}>
          <Newspaper size={28} className="text-[#0B3A8C]" weight="duotone" />
          <div className="text-xs text-zinc-500 mt-3">{m.date}</div>
          <h3 className="font-display font-bold text-lg mt-1">{m.title}</h3>
          <p className="text-sm text-zinc-600 mt-2 whitespace-pre-line">{m.body}</p>
        </article>
      ))}
    </div>
  </section>
  );
};

export const Careers = () => {
  const { items } = useSite("careers");
  return (
  <section id="careers" className="py-20 px-4 scroll-mt-24">
    <Title k="Careers" t="Join our teaching family" sub="Send your resume to [careers email address]." />
    <div className="max-w-4xl mx-auto space-y-3">
      {items.length === 0 && <p className="text-center text-zinc-500">No openings right now.</p>}
      {items.map((c, i) => (
        <div key={c.id} className="bg-white border border-zinc-200 rounded-xl p-5 flex items-center gap-4" data-testid={`career-item-${i}`}>
          <Briefcase size={28} className="text-[#E39A0B] shrink-0" weight="duotone" />
          <div className="flex-1">
            <div className="font-display font-bold">{c.role}</div>
            <div className="text-sm text-zinc-600">{c.req}</div>
          </div>
          <span className="text-xs font-semibold bg-blue-50 text-[#0B3A8C] rounded-full px-3 py-1">{c.type}</span>
        </div>
      ))}
    </div>
  </section>
  );
};

export const Achievements = () => {
  const { items } = useSite("achievements");
  return (
  <section id="achievements" className="py-20 px-4 bg-[#0B2A5C] text-white scroll-mt-24">
    <div className="text-center mb-10">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#F5B83D]">Our Achievements</div>
      <h2 className="font-display text-3xl md:text-4xl font-black mt-2">Proud moments, year after year</h2>
    </div>
    <div className="max-w-6xl mx-auto flex flex-wrap justify-center gap-6">
      {items.map((a, i) => (
        <div key={a.id} className="text-center bg-white/5 rounded-xl p-6 border border-white/10 w-full sm:w-[calc(50%-12px)] lg:w-80" data-testid={`achievement-${i}`}>
          <div className="font-display text-5xl font-black text-[#F5B83D]">{a.value}</div>
          <div className="font-display font-bold text-white mt-1">{a.label}</div>
          {a.desc && <p className="text-sm text-blue-100 mt-2">{a.desc}</p>}
        </div>
      ))}
    </div>
  </section>
  );
};

export const Contact = () => {
  const { schools } = useSchools();
  return (
  <section id="contact" className="py-20 px-4 bg-white scroll-mt-24">
    <Title k="Contact" t="Get in touch" />
    <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-5">
      {schools.map((s) => (
        <div key={s.slug} className="border border-zinc-200 rounded-xl p-6" data-testid={`contact-${s.slug}`}>
          <h3 className="font-display font-bold text-lg text-[#0B2A5C]">{s.name}</h3>
          <div className="mt-4 space-y-3 text-sm text-zinc-700">
            <div className="flex gap-3"><MapPin size={18} className="text-[#E39A0B] shrink-0" /> <span data-testid={`contact-address-${s.slug}`}>{s.address}</span></div>
            <div className="flex gap-3"><Phone size={18} className="text-[#E39A0B] shrink-0" />
              <span className="flex flex-wrap gap-x-3">
                {s.phones.map((p) => (
                  <a key={p} href={telHref(p)} className="hover:text-[#0B3A8C] transition-colors" data-testid={`contact-phone-${p}`}>{p}</a>
                ))}
              </span>
            </div>
            <div className="flex gap-3"><EnvelopeSimple size={18} className="text-[#E39A0B] shrink-0" />
              <a href={`mailto:${s.email}`} className="hover:text-[#0B3A8C] transition-colors break-all" data-testid={`contact-email-${s.slug}`}>{s.email}</a>
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
  );
};

export const Footer = () => (
  <footer className="bg-[#081F45] text-blue-100 text-sm py-8 px-4 text-center">
    © {new Date().getFullYear()} St. Thomas (Altitude) High School &amp; Astrid Rowe Memorial High School. All rights reserved.
  </footer>
);
